import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Form, Card, Badge, InputGroup, Button } from 'react-bootstrap';
import { Search, X } from 'lucide-react';

const SearchableMultiSelect = ({ label, options = [], selected = [], onChange, displayField = 'name', placeholder = "Buscar..." }) => {
    const [searchTerm, setSearchSearchTerm] = useState('');
    const [showOptions, setShowOptions] = useState(false);
    const containerRef = useRef(null);

    // Asegurar que options sea un array y filtrar nulos
    const safeOptions = useMemo(() => {
        return Array.isArray(options) ? options.filter(o => o !== null) : [];
    }, [options]);

    const filteredOptions = useMemo(() => {
        return safeOptions.filter(opt => 
            String(opt[displayField] || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [safeOptions, searchTerm, displayField]);

    const handleToggle = (id) => {
        if (selected.includes(id)) {
            onChange(selected.filter(x => x !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    const handleSelectAll = () => {
        const allFilteredIds = filteredOptions.map(opt => opt.id);
        const newSelected = Array.from(new Set([...selected, ...allFilteredIds]));
        onChange(newSelected);
    };

    const handleDeselectAll = () => {
        const allFilteredIds = filteredOptions.map(opt => opt.id);
        const newSelected = selected.filter(id => !allFilteredIds.includes(id));
        onChange(newSelected);
    };

    // Cerrar dropdown al hacer clic fuera del componente
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="position-relative w-100" ref={containerRef} style={{ zIndex: showOptions ? 1060 : 1 }}>
            {label && (
                <Form.Label className="small fw-bold text-muted text-uppercase d-flex justify-content-between align-items-center mb-1">
                    <span>{label}</span>
                    {selected.length > 0 && (
                        <Badge bg="primary" className="rounded-pill" style={{ fontSize: '9px' }}>
                            {selected.length} sel.
                        </Badge>
                    )}
                </Form.Label>
            )}
            <div 
                className="form-control form-control-sm bg-white d-flex justify-content-between align-items-center cursor-pointer shadow-sm"
                onClick={() => setShowOptions(!showOptions)}
                style={{ 
                    cursor: 'pointer', 
                    minHeight: '38px', 
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    padding: '6px 12px'
                }}
            >
                <div className="text-truncate text-muted small style-selected-text" style={{ maxWidth: '90%' }}>
                    {selected.length === 0 ? <span className="text-muted">{placeholder}</span> : 
                        safeOptions.filter(o => selected.includes(o.id)).map(o => o[displayField]).join(', ')
                    }
                </div>
                <div className="d-flex align-items-center gap-1">
                    {selected.length > 0 && (
                        <span 
                            onClick={(e) => { e.stopPropagation(); onChange([]); }}
                            style={{ cursor: 'pointer', opacity: 0.6, padding: '0 4px', fontSize: '11px' }}
                            title="Limpiar"
                        >
                            ✕
                        </span>
                    )}
                    {showOptions ? <X size={14} className="text-muted" /> : <Search size={14} className="text-muted" />}
                </div>
            </div>

            {showOptions && (
                <Card className="position-absolute shadow-lg border-0 z-index-popover mt-1" style={{ zIndex: 1060, width: '100%', minWidth: '320px', left: 0 }}>
                    <Card.Body className="p-2">
                        <InputGroup size="sm" className="mb-2">
                            <InputGroup.Text className="bg-white"><Search size={12} /></InputGroup.Text>
                            <Form.Control 
                                placeholder="Filtrar lista..." 
                                value={searchTerm}
                                onChange={(e) => setSearchSearchTerm(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </InputGroup>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {filteredOptions.length === 0 ? (
                                <div className="text-center py-2 text-muted small">No hay resultados</div>
                            ) : (
                                filteredOptions.map(opt => (
                                    <div 
                                        key={opt.id} 
                                        className="d-flex align-items-center px-2 py-1 border-bottom-light hover-bg-light rounded cursor-pointer"
                                        onClick={() => handleToggle(opt.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Form.Check 
                                            type="checkbox"
                                            id={`check-${label || 'multi'}-${opt.id}`}
                                            checked={selected.includes(opt.id)}
                                            onChange={() => {}} // Manejado por el div onClick
                                            className="small pointer-events-none me-2"
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <span className="small text-dark text-truncate">{opt[displayField]}</span>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top gap-1">
                            <div className="d-flex gap-1">
                                <Button variant="outline-primary" size="sm" className="py-1 px-2" onClick={handleSelectAll} style={{ fontSize: '10px' }}>
                                    Sel. Todos
                                </Button>
                                <Button variant="outline-secondary" size="sm" className="py-1 px-2" onClick={handleDeselectAll} style={{ fontSize: '10px' }}>
                                    Limpiar
                                </Button>
                            </div>
                            <Button variant="primary" size="sm" className="py-1 px-2" onClick={() => setShowOptions(false)} style={{ fontSize: '10px' }}>
                                Cerrar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default SearchableMultiSelect;
